// src/features/master-data/config/masterDataConfig.js
import * as yup from 'yup';
import {
  Users,
  UserCircle,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Shield,
  Pill,
  Lock,
  Activity,
  BadgeCheck,
  Clock,
  Globe,
  Hash,
  CheckCircle,
  XCircle,
  UserPlus,
  UserCheck,
  UserX,
  Key,
  Smartphone,
  Building2,
  Briefcase,
  Package,
  Scale,
  Tag,
  Box,
  Layers,
  GitBranch,
  Truck,
  Check,
   X 
} from 'lucide-react';

// Fix 1: Updated validation schemas with correct .when() syntax
export const validationSchemas = {
  categories: yup.object({
    name: yup.string()
      .required('Category name is required')
      .min(2, 'Name must be at least 2 characters')
      .max(50, 'Name must be less than 50 characters'),
    code: yup.string()
      .required('Category code is required')
      .matches(/^[A-Z0-9-]+$/, 'Code can only contain uppercase letters, numbers, and hyphens')
      .max(20, 'Code must be less than 20 characters'),
    description: yup.string()
      .nullable()
      .max(200, 'Description must be less than 200 characters'),
    is_active: yup.boolean().default(true),
    icon: yup.string().nullable(),
    color: yup.string().nullable(),
  }),
  medicines: yup.object({
    category_id: yup.number()
      .required('Category is required')
      .positive('Invalid category'),

    code: yup.string()
      .required('Medicine code is required')
      .matches(/^[A-Z0-9-]+$/, 'Code can only contain uppercase letters, numbers, and hyphens')
      .max(30, 'Code must be less than 30 characters'),

    name: yup.string()
      .required('Medicine name is required')
      .min(2)
      .max(100),

    generic_name: yup.string()
      .required('Generic name is required')
      .max(100),

    strength: yup.string()
      .required('Strength is required')
      .max(50),

    form: yup.string()
      .required('Dosage form is required')
      .oneOf(['Tablet', 'Capsule', 'Syrup', 'Injection', 'Ointment', 'Drops', 'Inhaler']),

    unit: yup.string()
      .required('Unit is required')
      .max(20),

    minimum_stock: yup.number()
      .required()
      .min(0),

    maximum_stock: yup.number()
      .required()
      .moreThan(yup.ref('minimum_stock'), 'Max stock must be greater than min stock'),

    reorder_level: yup.number()
      .required()
      .min(0),

    storage_conditions: yup.string()
      .nullable()
      .max(300),

    manufacturer: yup.string()
      .nullable()
      .max(150),

    requires_prescription: yup.boolean().default(false),
    is_controlled: yup.boolean().default(false),
    is_active: yup.boolean().default(true),

    description: yup.string().nullable().max(500),
    instructions: yup.string().nullable().max(500),
    side_effects: yup.string().nullable().max(500),
    contraindications: yup.string().nullable().max(500),
  }),
  units: yup.object({
    name: yup.string()
      .required('Unit name is required')
      .min(1, 'Name must be at least 1 character')
      .max(20, 'Name must be less than 20 characters'),
    code: yup.string()
      .required('Code is required')
      .max(5, 'Code must be less than 5 characters'),
    type: yup.string()
      .required('Unit type is required')
      .oneOf(['count', 'weight', 'volume', 'length', 'time'], 'Invalid unit type'),
    conversion_factor: yup.number()
      .nullable()
      .positive('Conversion factor must be positive')
      // Fixed: New syntax for .when() in Yup v1+
      .when('type', ([type], schema) => {
        return type && type !== 'count'
          ? schema.required('Conversion factor is required').positive()
          : schema.nullable();
      }),
    base_unit: yup.string()
      .nullable()
      .when('type', ([type], schema) => {
        return type && type !== 'count'
          ? schema.required('Base unit is required')
          : schema.nullable();
      }),
    description: yup.string()
      .nullable()
      .max(200, 'Description must be less than 200 characters'),
    is_active: yup.boolean().default(true),
  }),

  // Fixed: suppliers schema
  suppliers: yup.object({
    name: yup.string()
      .required('Supplier name is required')
      .min(2, 'Name must be at least 2 characters')
      .max(100, 'Name must be less than 100 characters'),
    code: yup.string()
      .required('Supplier code is required')
      .matches(/^[A-Z0-9-]+$/, 'Code can only contain uppercase letters, numbers, and hyphens')
      .max(20, 'Code must be less than 20 characters'),
    contact_person: yup.string()
      .nullable()
      .max(100, 'Contact person must be less than 100 characters'),
    email: yup.string()
      .nullable()
      .email('Invalid email format'),
    phone: yup.string()
      .nullable()
      .matches(/^[0-9+\-\s()]+$/, 'Invalid phone number format'),
    address: yup.string()
      .nullable()
      .max(500, 'Address must be less than 500 characters'),
    tax_id: yup.string()
      .nullable()
      .max(50, 'Tax ID must be less than 50 characters'),
    payment_terms: yup.string()
      .nullable()
      .max(100, 'Payment terms must be less than 100 characters'),
    is_active: yup.boolean().default(true),
  }),

  // Fixed: locations schema
  locations: yup.object({
    name: yup.string()
      .required('Location name is required')
      .min(2, 'Name must be at least 2 characters')
      .max(100, 'Name must be less than 100 characters'),
    code: yup.string()
      .required('Location code is required')
      .matches(/^[A-Z0-9-]+$/, 'Code can only contain uppercase letters, numbers, and hyphens')
      .max(20, 'Code must be less than 20 characters'),
    type: yup.string()
      .required('Location type is required')
      .oneOf(['warehouse', 'clinic', 'pharmacy', 'laboratory', 'office'], 'Invalid location type'),
    parent_id: yup.number()
      .nullable()
      .positive('Please select a valid parent location'),
    address: yup.string()
      .nullable()
      .max(500, 'Address must be less than 500 characters'),
    city: yup.string()
      .nullable()
      .max(100, 'City must be less than 100 characters'),
    state: yup.string()
      .nullable()
      .max(100, 'State must be less than 100 characters'),
    country: yup.string()
      .nullable()
      .max(100, 'Country must be less than 100 characters'),
    postal_code: yup.string()
      .nullable()
      .max(20, 'Postal code must be less than 20 characters'),
    is_active: yup.boolean().default(true),
  }),

  // Fixed: manufacturers schema
  manufacturers: yup.object({
    name: yup.string()
      .required('Manufacturer name is required')
      .min(2, 'Name must be at least 2 characters')
      .max(100, 'Name must be less than 100 characters'),
    code: yup.string()
      .required('Manufacturer code is required')
      .matches(/^[A-Z0-9-]+$/, 'Code can only contain uppercase letters, numbers, and hyphens')
      .max(20, 'Code must be less than 20 characters'),
    country: yup.string()
      .nullable()
      .max(100, 'Country must be less than 100 characters'),
    website: yup.string()
      .nullable()
      .url('Invalid URL format'),
    is_active: yup.boolean().default(true),
  }),

  // Fixed: departments schema
  departments: yup.object({
    name: yup.string()
      .required('Department name is required')
      .min(2, 'Name must be at least 2 characters')
      .max(100, 'Name must be less than 100 characters'),
    code: yup.string()
      .required('Department code is required')
      .matches(/^[A-Z0-9-]+$/, 'Code can only contain uppercase letters, numbers, and hyphens')
      .max(20, 'Code must be less than 20 characters'),
    head_of_department: yup.string()
      .nullable()
      .max(100, 'Head of department must be less than 100 characters'),
    parent_id: yup.number()
      .nullable()
      .positive('Please select a valid parent department'),
    location_id: yup.number()
      .nullable()
      .positive('Please select a valid location'),
    is_active: yup.boolean().default(true),
  }),

  users: yup.object({
    username: yup.string()
      .required('Username is required')
      .min(3, 'Username must be at least 3 characters')
      .max(50, 'Username must be less than 50 characters')
      .matches(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores'),
    
    email: yup.string()
      .required('Email is required')
      .email('Invalid email format')
      .max(100, 'Email must be less than 100 characters'),
    
    first_name: yup.string()
      .required('First name is required')
      .min(2, 'First name must be at least 2 characters')
      .max(50, 'First name must be less than 50 characters'),
    
    last_name: yup.string()
      .nullable()
      .max(50, 'Last name must be less than 50 characters'),
    
    middle_name: yup.string()
      .nullable()
      .max(50, 'Middle name must be less than 50 characters'),
    
    phone: yup.string()
      .nullable()
      .matches(/^[0-9+\-\s()]+$/, 'Invalid phone number format')
      .max(20, 'Phone number must be less than 20 characters'),
    
    mobile: yup.string()
      .nullable()
      .matches(/^[0-9+\-\s()]+$/, 'Invalid mobile number format')
      .max(20, 'Mobile number must be less than 20 characters'),
    
    gender: yup.string()
      .nullable()
      .oneOf(['male', 'female', 'other'], 'Invalid gender'),
    
    date_of_birth: yup.date()
      .nullable()
      .max(new Date(), 'Date of birth cannot be in the future'),
    
    user_type: yup.string()
      .required('User type is required')
      .oneOf(['super_admin', 'admin', 'ohc_staff', 'nurse', 'doctor', 'patient', 'hr', 'manager'], 'Invalid user type'),
    
    status: yup.string()
      .required('Status is required')
      .oneOf(['active', 'inactive', 'suspended', 'pending'], 'Invalid status')
      .default('active'),
    
    ohc_id: yup.number()
      .nullable()
      .positive('Invalid OHC selection'),
    
    organization_id: yup.number()
      .nullable()
      .positive('Invalid organization selection'),
    
    is_password_change_required: yup.boolean().default(false),
    
    preferences: yup.object()
      .nullable()
      .default({}),
  }),
};

// Define table columns (unchanged, but fixed the status renderer)
export const tableColumns = {
  categories: [
    { key: 'code', title: 'Code', sortable: true },
    { key: 'name', title: 'Name', sortable: true },
    {
      key: 'is_active',
      title: 'Status',
      sortable: true,
      render: (value) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    //{ key: 'created_at', title: 'Created', sortable: true },
  ],
  medicines: [
    { key: 'code', title: 'Code', sortable: true },
    { key: 'name', title: 'Name', sortable: true },
    { key: 'generic_name', title: 'Generic Name' },
    { key: 'strength', title: 'Strength' },
    { key: 'form', title: 'Form' },
    { key: 'unit', title: 'Unit' },

    { key: 'requires_prescription',title: 'Rx',
      render: (value) => (
    <span className={`inline-flex items-center justify-center px-2 py-1 rounded-full ${
      value ? 'bg-green-100' : 'bg-red-100'
    }`}>
      {value ? (
        <Check className="h-4 w-4 text-green-600" />
      ) : (
        <X className="h-4 w-4 text-red-600" />
      )}
    </span>
  )  
    },

    {
      key: 'is_controlled',
      title: 'Controlled',
      //render: (value) => value ? 'Yes' : 'No'
         render: (value) => (
    <span className={`inline-flex items-center justify-center px-2 py-1 rounded-full ${
      value ? 'bg-green-100' : 'bg-red-100'
    }`}>
      {value ? (
        <Check className="h-4 w-4 text-green-600" />
      ) : (
        <X className="h-4 w-4 text-red-600" />
      )}
    </span>
  ) 
    },

    {
      key: 'is_active',
      title: 'Status',
      render: (value) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },

    // { key: 'created_at', title: 'Created', sortable: true },
  ],

  units: [
    { key: 'name', title: 'Name', sortable: true },
    { key: 'code', title: 'Symbol' },
    { key: 'description', title: 'Description' },
    {
      key: 'is_active',
      title: 'Status',
      render: (value) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
  ],

  suppliers: [
    { key: 'code', title: 'Code', sortable: true },
    { key: 'name', title: 'Name', sortable: true },
    { key: 'contact_person', title: 'Contact Person' },
    { key: 'email', title: 'Email' },
    { key: 'phone', title: 'Phone' },
    {
      key: 'is_active',
      title: 'Status',
      render: (value) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
  ],

  locations: [
    { key: 'code', title: 'Code', sortable: true },
    { key: 'name', title: 'Name', sortable: true },
    {
      key: 'type',
      title: 'Type',
      render: (value) => value?.charAt(0).toUpperCase() + value?.slice(1)
    },
    { key: 'city', title: 'City' },
    { key: 'state', title: 'State' },
    {
      key: 'is_active',
      title: 'Status',
      render: (value) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
  ],

  manufacturers: [
    { key: 'code', title: 'Code', sortable: true },
    { key: 'name', title: 'Name', sortable: true },
    { key: 'country', title: 'Country' },
    {
      key: 'is_active',
      title: 'Status',
      render: (value) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
  ],

  departments: [
    { key: 'code', title: 'Code', sortable: true },
    { key: 'name', title: 'Name', sortable: true },
    { key: 'head_of_department', title: 'Head' },
    { key: 'parent_name', title: 'Parent Department' },
    {
      key: 'is_active',
      title: 'Status',
      render: (value) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
  ],
   users: [
    { 
      key: 'username', 
      title: 'Username', 
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <UserCircle className="h-5 w-5 text-gray-400" />
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="text-xs text-gray-500">{row.email}</div>
          </div>
        </div>
      )
    },
    { 
      key: 'first_name', 
      title: 'Full Name', 
      sortable: true,
      render: (value, row) => `${row.first_name} ${row.last_name || ''}`.trim()
    },
    { 
      key: 'user_type', 
      title: 'User Type', 
      sortable: true,
      render: (value) => {
        const types = {
          super_admin: 'Super Admin',
          admin: 'Admin',
          ohc_staff: 'OHC Staff',
          nurse: 'Nurse',
          doctor: 'Doctor',
          patient: 'Patient',
          hr: 'HR',
          manager: 'Manager'
        };
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
            {types[value] || value}
          </span>
        );
      }
    },
    { 
      key: 'mobile', 
      title: 'Mobile', 
      render: (value) => value || '-'
    },
    { 
      key: 'gender', 
      title: 'Gender',
      render: (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : '-'
    },
    { 
      key: 'last_login_at', 
      title: 'Last Login', 
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleDateString() : 'Never'
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      render: (value) => {
        const statusConfig = {
          active: { class: 'bg-green-100 text-green-800', label: 'Active' },
          inactive: { class: 'bg-gray-100 text-gray-800', label: 'Inactive' },
          suspended: { class: 'bg-red-100 text-red-800', label: 'Suspended' },
          pending: { class: 'bg-yellow-100 text-yellow-800', label: 'Pending' }
        };
        const config = statusConfig[value] || statusConfig.inactive;
        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.class}`}>
            {config.label}
          </span>
        );
      }
    },
    { 
      key: 'created_at', 
      title: 'Created', 
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString()
    },
  ],

};

// Define form fields (updated with proper icons)
export const formFields = {
  categories: [
    { name: 'name', label: 'Category Name', type: 'text', required: true, icon: Package },
    { name: 'code', label: 'Category Code', type: 'text', required: true, icon: Hash },
    { name: 'description', label: 'Description', type: 'textarea', rows: 3 },
    { name: 'icon', label: 'Icon Name', type: 'text', helperText: 'Lucide icon name' },
    { name: 'color', label: 'Color', type: 'text', placeholder: '#3b82f6' },
    { name: 'is_active', label: 'Active', type: 'checkbox', default: true },
  ],
  medicines: [
    {
      name: 'category_id', label: 'Category', type: 'relation', // 👈 NEW TYPE
      model: 'categories', // API endpoint
      dependsOn: ['ohc_id'], // 👈 dependency
      required: true
    },


    { name: 'code', label: 'Medicine Code', type: 'text', required: true, icon: Hash },

    { name: 'name', label: 'Brand Name', type: 'text', required: true, icon: Pill },

    { name: 'generic_name', label: 'Generic Name', type: 'text', required: true, icon: Pill },

    { name: 'strength', label: 'Strength', type: 'text', placeholder: '500 mg' },

    {
      name: 'form',
      label: 'Dosage Form',
      type: 'select',
      required: true,
      options: [
        { value: 'Tablet', label: 'Tablet' },
        { value: 'Capsule', label: 'Capsule' },
        { value: 'Syrup', label: 'Syrup' },
        { value: 'Injection', label: 'Injection' },
        { value: 'Ointment', label: 'Ointment' },
        { value: 'Drops', label: 'Drops' },
        { value: 'Inhaler', label: 'Inhaler' },
      ]
    },

    // { name: 'unit', label: 'Unit', type: 'text', required: true, placeholder: 'Strip / Bottle' },
    {
      name: 'unit', label: 'Unit', type: 'relation', // 👈 NEW TYPE
      model: 'units', // API endpoint
      dependsOn: ['units'], // 👈 dependency
      required: true
    },

    { name: 'minimum_stock', label: 'Minimum Stock', type: 'number' },
    { name: 'maximum_stock', label: 'Maximum Stock', type: 'number' },
    { name: 'reorder_level', label: 'Reorder Level', type: 'number' },

    { name: 'manufacturer', label: 'Manufacturer', type: 'text' },

    { name: 'storage_conditions', label: 'Storage Conditions', type: 'textarea', rows: 2 },

    { name: 'instructions', label: 'Usage Instructions', type: 'textarea', rows: 2 },
    { name: 'side_effects', label: 'Side Effects', type: 'textarea', rows: 2 },
    { name: 'contraindications', label: 'Contraindications', type: 'textarea', rows: 2 },

    { name: 'description', label: 'Description', type: 'textarea', rows: 2 },

    { name: 'requires_prescription', label: 'Requires Prescription', type: 'checkbox' },
    { name: 'is_controlled', label: 'Controlled Drug', type: 'checkbox' },
    { name: 'is_active', label: 'Active', type: 'checkbox', default: true },
  ],

  units: [
    { name: 'name', label: 'Unit Name', type: 'text', required: true, icon: Scale },
    { name: 'code', label: 'Symbol', type: 'text', required: true, icon: Tag },
    { name: 'description', label: 'Description', type: 'textarea', rows: 3 },
    {
      name: 'type',
      label: 'Unit Type',
      type: 'select',
      required: true,
      options: [
        { value: 'count', label: 'Count' },
        { value: 'weight', label: 'Weight' },
        { value: 'volume', label: 'Volume' },
        { value: 'length', label: 'Length' },
        { value: 'time', label: 'Time' },
      ],
      icon: Box
    },
    // { 
    //   name: 'conversion_factor', 
    //   label: 'Conversion Factor', 
    //   type: 'number', 
    //   step: '0.001',
    //   dependsOn: { field: 'type', values: ['weight', 'volume', 'length', 'time'] }
    // },
    // { 
    //   name: 'base_unit', 
    //   label: 'Base Unit', 
    //   type: 'text',
    //   dependsOn: { field: 'type', values: ['weight', 'volume', 'length', 'time'] }
    // },
    { name: 'is_active', label: 'Active', type: 'checkbox', default: true },
  ],


  suppliers: [
    { name: 'name', label: 'Supplier Name', type: 'text', required: true, icon: Building2 },
    { name: 'code', label: 'Supplier Code', type: 'text', required: true, icon: Hash },
    { name: 'contact_person', label: 'Contact Person', type: 'text', icon: UserCircle },
    { name: 'email', label: 'Email', type: 'email', icon: Mail },
    { name: 'phone', label: 'Phone', type: 'text', icon: Phone },
    { name: 'address', label: 'Address', type: 'textarea', rows: 2 },
    { name: 'tax_id', label: 'Tax ID', type: 'text' },
    { name: 'payment_terms', label: 'Payment Terms', type: 'text' },
    { name: 'is_active', label: 'Active', type: 'checkbox', default: true },
  ],

  locations: [
    { name: 'name', label: 'Location Name', type: 'text', required: true, icon: MapPin },
    { name: 'code', label: 'Location Code', type: 'text', required: true, icon: Hash },
    {
      name: 'type',
      label: 'Location Type',
      type: 'select',
      required: true,
      options: [
        { value: 'warehouse', label: 'Warehouse' },
        { value: 'clinic', label: 'Clinic' },
        { value: 'pharmacy', label: 'Pharmacy' },
        { value: 'laboratory', label: 'Laboratory' },
        { value: 'office', label: 'Office' },
      ],
      icon: Building2
    },
    {
      name: 'parent_id',
      label: 'Parent Location',
      type: 'select',
      options: [],
      icon: Layers
    },
    { name: 'address', label: 'Address', type: 'textarea', rows: 2 },
    { name: 'city', label: 'City', type: 'text' },
    { name: 'state', label: 'State', type: 'text' },
    { name: 'country', label: 'Country', type: 'text' },
    { name: 'postal_code', label: 'Postal Code', type: 'text' },
    { name: 'is_active', label: 'Active', type: 'checkbox', default: true },
  ],

  manufacturers: [
    { name: 'name', label: 'Manufacturer Name', type: 'text', required: true, icon: Building2 },
    { name: 'code', label: 'Manufacturer Code', type: 'text', required: true, icon: Hash },
    { name: 'country', label: 'Country', type: 'text', icon: Globe },
    { name: 'website', label: 'Website', type: 'url', icon: Globe },
    { name: 'is_active', label: 'Active', type: 'checkbox', default: true },
  ],

  departments: [
    { name: 'name', label: 'Department Name', type: 'text', required: true, icon: Users },
    { name: 'code', label: 'Department Code', type: 'text', required: true, icon: Hash },
    { name: 'head_of_department', label: 'Head of Department', type: 'text', icon: UserCircle },
    {
      name: 'parent_id',
      label: 'Parent Department',
      type: 'select',
      options: [],
      icon: GitBranch
    },
    {
      name: 'location_id',
      label: 'Location',
      type: 'select',
      options: [],
      icon: MapPin
    },
    { name: 'is_active', label: 'Active', type: 'checkbox', default: true },
  ],
  users: [
    // Personal Information Section
    {
      section: 'Personal Information',
      fields: [
        { 
          name: 'first_name', 
          label: 'First Name', 
          type: 'text', 
          required: true, 
          icon: UserCircle,
          placeholder: 'Enter first name'
        },
        { 
          name: 'middle_name', 
          label: 'Middle Name', 
          type: 'text', 
          icon: UserCircle,
          placeholder: 'Enter middle name (optional)'
        },
        { 
          name: 'last_name', 
          label: 'Last Name', 
          type: 'text', 
          icon: UserCircle,
          placeholder: 'Enter last name (optional)'
        },
        { 
          name: 'gender', 
          label: 'Gender', 
          type: 'select',
          options: [
            { value: 'male', label: 'Male' },
            { value: 'female', label: 'Female' },
            { value: 'other', label: 'Other' }
          ],
          icon: UserCircle
        },
        { 
          name: 'date_of_birth', 
          label: 'Date of Birth', 
          type: 'date', 
          icon: Calendar
        },
      ]
    },
    
    // Account Information Section
    {
      section: 'Account Information',
      fields: [
        { 
          name: 'username', 
          label: 'Username', 
          type: 'text', 
          required: true, 
          icon: Hash,
          placeholder: 'Enter username',
          helperText: 'Letters, numbers, and underscores only'
        },
        { 
          name: 'email', 
          label: 'Email Address', 
          type: 'email', 
          required: true, 
          icon: Mail,
          placeholder: 'user@example.com'
        },
        { 
          name: 'mobile', 
          label: 'Mobile Number', 
          type: 'text', 
          icon: Smartphone,
          placeholder: '+1234567890'
        },
        { 
          name: 'phone', 
          label: 'Phone Number', 
          type: 'text', 
          icon: Phone,
          placeholder: 'Optional'
        },
      ]
    },
    
    // Role & Permissions Section
    {
      section: 'Role & Permissions',
      fields: [
        { 
          name: 'user_type', 
          label: 'User Type', 
          type: 'select', 
          required: true,
          options: [
            { value: 'super_admin', label: 'Super Admin' },
            { value: 'admin', label: 'Admin' },
            { value: 'ohc_staff', label: 'OHC Staff' },
            { value: 'nurse', label: 'Nurse' },
            { value: 'doctor', label: 'Doctor' },
            { value: 'patient', label: 'Patient' },
            { value: 'hr', label: 'HR' },
            { value: 'manager', label: 'Manager' }
          ],
          icon: Shield
        },
        { 
          name: 'ohc_id', 
          label: 'OHC', 
          type: 'relation',
          model: 'ohcs',
          dependsOn: ['user_type'],
          condition: (formData) => ['ohc_staff', 'nurse', 'doctor'].includes(formData.user_type),
          icon: Building2
        },
        { 
          name: 'organization_id', 
          label: 'Organization', 
          type: 'relation',
          model: 'organizations',
          icon: Briefcase
        },
      ]
    },
    
    // Security Settings Section
    {
      section: 'Security Settings',
      fields: [
        { 
          name: 'status', 
          label: 'Account Status', 
          type: 'select',
          required: true,
          options: [
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'suspended', label: 'Suspended' },
            { value: 'pending', label: 'Pending' }
          ],
          icon: Activity,
          default: 'active'
        },
        { 
          name: 'is_password_change_required', 
          label: 'Require Password Change on Next Login', 
          type: 'checkbox',
          icon: Key,
          default: false
        },
      ]
    },
    
    // Preferences Section
    {
      section: 'Preferences',
      fields: [
        { 
          name: 'preferences', 
          label: 'User Preferences', 
          type: 'json',
          fields: [
            { name: 'theme', label: 'Theme', type: 'select', options: [
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
              { value: 'system', label: 'System' }
            ]},
            { name: 'language', label: 'Language', type: 'select', options: [
              { value: 'en', label: 'English' },
              { value: 'es', label: 'Spanish' },
              { value: 'fr', label: 'French' }
            ]},
            { name: 'notifications', label: 'Email Notifications', type: 'checkbox' },
            { name: 'dashboard_layout', label: 'Dashboard Layout', type: 'text' }
          ]
        },
      ]
    }
  ],
};

// Define API endpoints for each master data type
export const apiEndpoints = {
  categories: '/stock/categories',
  medicines: '/stock/medicines',
  units: '/measurement-units',
  suppliers: '/master/suppliers',
  locations: '/master/locations',
  manufacturers: '/master/manufacturers',
  departments: '/master/departments',
  
  users: '/users',
  ohcs: '/ohcs',
  organizations: '/organizations',
};

// Define display configurations
export const displayConfig = {
  categories: {
    title: 'Stock Categories',
    description: 'Manage product categories for inventory',
    icon: Package,
    color: 'blue',
  },
  medicines: {
    title: 'Stock Medicines',
    description: 'Manage medicines for inventory',
    icon: Pill,
    color: 'blue',
  },
  units: {
    title: 'Units of Measure',
    description: 'Manage measurement units for products',
    icon: Scale,
    color: 'green',
  },
  suppliers: {
    title: 'Suppliers',
    description: 'Manage vendor and supplier information',
    icon: Truck,
    color: 'purple',
  },
  locations: {
    title: 'Locations',
    description: 'Manage storage locations and facilities',
    icon: MapPin,
    color: 'orange',
  },
  manufacturers: {
    title: 'Manufacturers',
    description: 'Manage product manufacturers',
    icon: Building2,
    color: 'indigo',
  },
  departments: {
    title: 'Departments',
    description: 'Manage organizational departments',
    icon: Users,
    color: 'pink',
  },
  users: {
    title: 'User Management',
    description: 'Manage system users, roles, and permissions',
    icon: Users,
    color: 'purple',
    actions: ['create', 'edit', 'delete', 'view', 'reset_password', 'activate', 'suspend'],
    bulkActions: ['activate', 'suspend', 'delete'],
    filters: [
      { field: 'user_type', label: 'User Type', type: 'select', options: [
        { value: 'super_admin', label: 'Super Admin' },
        { value: 'admin', label: 'Admin' },
        { value: 'ohc_staff', label: 'OHC Staff' },
        { value: 'nurse', label: 'Nurse' },
        { value: 'doctor', label: 'Doctor' },
        { value: 'patient', label: 'Patient' },
        { value: 'hr', label: 'HR' },
        { value: 'manager', label: 'Manager' }
      ]},
      { field: 'status', label: 'Status', type: 'select', options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'suspended', label: 'Suspended' },
        { value: 'pending', label: 'Pending' }
      ]},
      { field: 'gender', label: 'Gender', type: 'select', options: [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'other', label: 'Other' }
      ]},
    ],
    searchFields: ['username', 'email', 'first_name', 'last_name', 'mobile'],
    exportFields: ['username', 'email', 'first_name', 'last_name', 'user_type', 'status', 'last_login_at'],
  },
};

// Additional user-specific utilities
export const userUtils = {
  // Get user full name
  getFullName: (user) => {
    const parts = [user.first_name];
    if (user.middle_name) parts.push(user.middle_name);
    if (user.last_name) parts.push(user.last_name);
    return parts.join(' ');
  },
  
  // Get user initials
  getInitials: (user) => {
    const first = user.first_name?.charAt(0) || '';
    const last = user.last_name?.charAt(0) || '';
    return (first + last).toUpperCase();
  },
  
  // Check if user has specific role
  hasRole: (user, roles) => {
    return Array.isArray(roles) ? roles.includes(user.user_type) : user.user_type === roles;
  },
  
  // Check if user account is active
  isActive: (user) => {
    return user.status === 'active';
  },
  
  // Get user type label
  getUserTypeLabel: (type) => {
    const labels = {
      super_admin: 'Super Admin',
      admin: 'Administrator',
      ohc_staff: 'OHC Staff',
      nurse: 'Nurse',
      doctor: 'Doctor',
      patient: 'Patient',
      hr: 'Human Resources',
      manager: 'Manager'
    };
    return labels[type] || type;
  },
  
  // Get status badge configuration
  getStatusBadge: (status) => {
    const config = {
      active: { class: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Active' },
      inactive: { class: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Inactive' },
      suspended: { class: 'bg-red-100 text-red-800', icon: UserX, label: 'Suspended' },
      pending: { class: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' }
    };
    return config[status] || config.inactive;
  },
  
  // Format last login
  formatLastLogin: (date) => {
    if (!date) return 'Never logged in';
    const now = new Date();
    const lastLogin = new Date(date);
    const diffDays = Math.floor((now - lastLogin) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return lastLogin.toLocaleDateString();
  },
  
  // Get user permissions based on role
  getPermissions: (userType) => {
    const permissions = {
      super_admin: ['*'], // All permissions
      admin: ['manage_users', 'manage_medicines', 'view_reports'],
      ohc_staff: ['manage_patients', 'manage_prescriptions', 'view_medicines'],
      nurse: ['manage_patients', 'view_prescriptions', 'administer_medicines'],
      doctor: ['manage_prescriptions', 'view_patients', 'view_medicines'],
      patient: ['view_own_profile', 'view_own_prescriptions'],
      hr: ['manage_staff', 'view_reports'],
      manager: ['view_reports', 'manage_departments']
    };
    return permissions[userType] || [];
  }
};